/**
 * @file     types.ts
 * @purpose  Type definitions for the mortgage calculator flow state, step names, and options.
 */

import type { MortgageCalcStepName } from '@/core/types';
import { POSTED_FIVE_YEAR_RATE_PCT } from '@/core/finance/mortgage';
import type { PaymentFrequency } from '@/core/finance/mortgageMath';
export type { MortgageCalcStepName } from '@/core/types';

export type MortgageCalcQuestionStepName = Exclude<
  MortgageCalcStepName,
  'results' | 'leadCapture' | 'nextSteps'
>;

export type MortgageFlowState = {
  homePrice: number;
  downPayment: number;
  downPaymentPercent: number;
  amortizationYears: 25 | 30;
  interestRate: number;
  annualPropertyTax: number;
  annualInsurance: number;
  monthlyHoa: number;
  frequency: PaymentFrequency;
  /** Extra principal paid every month. */
  extraMonthlyPrepayment: number;
  /** One-time lump sum, paid at the start of `lumpSumYear`. */
  lumpSumAmount: number;
  lumpSumYear: number;
  usedEstimates: boolean;
  leadName: string;
  leadEmail: string;
  leadPhone: string;
  ctaChoice: 'help-me-buy' | 'do-it-myself' | null;
};

export type MortgageCalcOption<TValue extends string = string> = {
  value: TValue;
  label: string;
  description?: string;
};

/** Vancouver-area sensible defaults for the calculator. */
export const MORTGAGE_DEFAULTS: MortgageFlowState = {
  homePrice: 1100000,
  downPayment: 220000,
  downPaymentPercent: 20,
  amortizationYears: 25,
  interestRate: POSTED_FIVE_YEAR_RATE_PCT,
  annualPropertyTax: 4200,
  annualInsurance: 1800,
  monthlyHoa: 350,
  frequency: 'monthly',
  extraMonthlyPrepayment: 0,
  lumpSumAmount: 0,
  lumpSumYear: 1,
  usedEstimates: true,
  leadName: '',
  leadEmail: '',
  leadPhone: '',
  ctaChoice: null,
};
